import { IConversation, IConversationDetails } from '../models/conversation.models';

/**
 * The list shape, as a runtime key set.
 *
 * Typed `Record<keyof IConversation, true>` on purpose: TypeScript cannot derive a runtime
 * key list from an interface, so this is the only way to make the compiler reject the set
 * going stale. Add a field to `IConversation` and this object fails to build until it is
 * listed here — which is what stops by-id list entries from quietly missing a field.
 */
const CONVERSATION_LIST_KEYS: Record<keyof IConversation, true> = {
  id: true,
  subject: true,
  sender: true,
  sender_email: true,
  mailbox: true,
  provider_thread_id: true,
  last_message_at: true,
  message_count: true,
  attachment_count: true,
  is_read: true,
  assigned_to_id: true,
  urgency: true,
  is_urgent: true,
  classification: true,
  categories: true,
  status: true,
  assignee: true,
  customer: true,
};

/**
 * Narrows a thread fetched from the details endpoint down to the list shape.
 *
 * `IConversationDetails` is a superset of `IConversation`, so it would go into the list
 * untouched — but it drags the whole timeline with it. The list only ever reads the card
 * fields, so drop the rest and keep list entries uniform whether they came from `/threads`
 * or from a by-id fetch.
 *
 * Picks the known keys rather than omitting the known extras: omission fails open, so a
 * field added to `IConversationDetails` later would leak into the list silently. This
 * direction fails closed, and the compiler enforces the key set above.
 */
export function toListConversation(details: IConversationDetails): IConversation {
  const conversation: Partial<Record<keyof IConversation, unknown>> = {};
  for (const key of Object.keys(CONVERSATION_LIST_KEYS) as (keyof IConversation)[]) {
    if (key in details) conversation[key] = details[key];
  }
  return conversation as IConversation;
}
